import { z } from 'zod';

import { RssUpdatePeriod } from '@/entities/content/api/types';
import { PlayerStylesConfigSchema } from '@/features/content-styles-form/lib/schema';

import {
  rssSettingsFirstUpdateValidator,
  rssSettingsPeriodValidator,
  textBlocksValidator,
  textBlockValidator,
} from './validators';

export const PublicationSettingsSchema = z.object({
  name: z.string().trim().default(''),
  sites: z
    .array(z.number())
    .nullish()
    .transform((value) => value ?? []),
  zones: z
    .array(z.number())
    .nullish()
    .transform((value) => value ?? []),
  publication: z
    .number()
    .min(0)
    .max(1)
    .nullish()
    .transform((value) => value ?? 0),
  publication_urls: z.string().trim().default(''),
  // why?
  categories: z
    .array(z.number())
    .nullish()
    .transform((value) => value ?? []),
});

const RssSettingSchema = z.object({
  link: z
    .string()
    .trim()
    .optional()
    .refine(
      (val) =>
        val === undefined || val === '' || z.url().safeParse(val).success,
      {
        error: 'Invalid URL.',
      }
    ),
  limit: z.coerce.number<number>(),
  auto_update: z
    .object({
      enabled: z.boolean(),
      time: z.string().optional(),
      interval_days: z.enum(RssUpdatePeriod).optional(),
    })
    .refine(rssSettingsFirstUpdateValidator, {
      path: ['time'],
      error: 'This field is required.',
    })
    .refine(rssSettingsPeriodValidator, {
      path: ['interval_days'],
      error: 'This field is required.',
    }),
});

const SlideSchema = z.object({
  image: z.string().trim().default(''),
  title: z.string().trim().default(''),
  link: z.string().trim().default(''),
});

export const TextBlockSchema = z
  .object({
    start: z.int().nonnegative(),
    end: z.int().positive(),
    content: z.string().min(3),
    url: z.string().trim().optional(),
  })
  .refine(textBlockValidator, {
    path: ['start'],
    error: 'Start time must be before end time.',
  });

export const SliderContentSchema = z.object({
  id: z.coerce.number().optional(),
  type: z.literal('slider'),
  status: z.number().min(0).max(1),
  ...PublicationSettingsSchema.shape,
  config: PlayerStylesConfigSchema,
  rss_settings: RssSettingSchema.optional(),
  slides: z
    .array(SlideSchema)
    .nullish()
    .transform((value) => value ?? []),
});

export const VideoContentSchema = z.object({
  id: z.coerce.number().optional(),
  type: z.literal('video'),
  status: z.number().min(0).max(1),
  ...PublicationSettingsSchema.shape,
  config: PlayerStylesConfigSchema,
  editor_config: z
    .string()
    // .refine(editorConfigValidator, {
    //   error: 'Cannot render an empty project.',
    // })
    .optional(),
  text_blocks: z
    .array(TextBlockSchema)
    .nullish()
    .transform((value) => value ?? [])
    .refine(textBlocksValidator, {
      path: ['text_blocks'],
      error: 'Text blocks cannot overlap in time.',
    }),
});

const SlideSchemaForPublication = z.object({
  image: z.url().trim(),
  title: z.string().trim().default(''),
  link: z.url().trim(),
});

export const SliderContentPublicationSchema = SliderContentSchema.safeExtend({
  sites: z.array(z.number()).min(1, {
    error: 'Select at least one site.',
  }),
  zones: z.array(z.number()).min(1, {
    error: 'Select at least one ad unit.',
  }),
  slides: z.array(SlideSchemaForPublication).min(1, {
    error: 'Add at least one slide.',
  }),
});

export const VideoContentPublicationSchema = VideoContentSchema.safeExtend({
  sites: z.array(z.number()).min(1, {
    error: 'Select at least one site.',
  }),
  zones: z.array(z.number()).min(1, {
    error: 'Select at least one ad unit.',
  }),
});

export type SliderContent = z.infer<typeof SliderContentSchema>;
export type SliderContentIn = z.input<typeof SliderContentSchema>;
export type SliderContentOut = z.output<typeof SliderContentSchema>;

export type VideoContent = z.infer<typeof VideoContentSchema>;
export type VideoContentIn = z.input<typeof VideoContentSchema>;
export type VideoContentOut = z.output<typeof VideoContentSchema>;
